/**
 * WebMCP 初始化模块
 * 为 AI Tetris 提供 WebMCP 协议支持
 *
 * 支持多种访问方式：
 * - MCP-B Global: 通过 navigator.modelContext 接口
 * - MCP-B Polyfill: 不支持原生 WebMCP 时的降级方案
 * - Local Relay: 通过 embed.js 连接到本地 relay 服务器
 * - DevTools MCP: 通过 Chrome DevTools Protocol
 * - Playwright/Selenium: 通过 executeScript/evaluate
 */
/* eslint-disable */
var tools = require('./tools').tools;
var resources = require('./resources').resources;
var t = require('./lang').t;

/**
 * 注入 sr-only 样式（Screen Reader Only）
 * 用于对人类用户隐藏但对 AI Agent 可见的元素
 */
function injectSrOnlyStyles() {
  if (document.getElementById('sr-only-styles')) {
    return; // 样式已存在
  }

  var style = document.createElement('style');
  style.id = 'sr-only-styles';
  style.textContent = [
    '.sr-only {',
    '  position: absolute;',
    '  width: 1px;',
    '  height: 1px;',
    '  padding: 0;',
    '  margin: -1px;',
    '  overflow: hidden;',
    '  clip: rect(0, 0, 0, 0);',
    '  white-space: nowrap;',
    '  border: 0;',
    '}',
  ].join('\n');

  document.head.appendChild(style);
}

/**
 * Page→Agent 通信辅助函数
 *
 * 支持的通信方式：
 * 1. window.agentCallback - Playwright exposeFunction
 * 2. window.agentBridge - CDP Runtime.addBinding
 * 3. window.callAgent - Selenium BiDi binding
 * 4. window.notifyAgent - 通用回调
 *
 * @param {Object} data - 要发送的数据
 * @returns {boolean} - 是否成功发送
 */
function notifyAgent(data) {
  // 方式 1: Playwright exposeFunction
  if (typeof window.agentCallback === 'function') {
    try {
      window.agentCallback(data);
      console.log('[WebMCP] 已通过 agentCallback 发送:', data);
      return true;
    } catch (e) {
      console.error('[WebMCP] agentCallback 调用失败:', e);
    }
  }

  // 方式 2: CDP Runtime.addBinding
  if (typeof window.agentBridge === 'function') {
    try {
      window.agentBridge(JSON.stringify(data));
      console.log('[WebMCP] 已通过 agentBridge 发送:', data);
      return true;
    } catch (e) {
      console.error('[WebMCP] agentBridge 调用失败:', e);
    }
  }

  // 方式 3: Selenium BiDi binding
  if (typeof window.callAgent === 'function') {
    try {
      window.callAgent(data);
      console.log('[WebMCP] 已通过 callAgent 发送:', data);
      return true;
    } catch (e) {
      console.error('[WebMCP] callAgent 调用失败:', e);
    }
  }

  // 方式 4: 通用 notifyAgent 回调(排除自身——window.notifyAgent 初始指向本函数,
  // 无外部回调时直接调用会无限递归爆栈:Maximum call stack size exceeded)
  if (typeof window.notifyAgent === 'function' && window.notifyAgent !== notifyAgent) {
    try {
      window.notifyAgent(data);
      console.log('[WebMCP] 已通过 notifyAgent 发送:', data);
      return true;
    } catch (e) {
      console.error('[WebMCP] notifyAgent 调用失败:', e);
    }
  }

  // 未检测到回调，记录到控制台
  console.log('[WebMCP] 未检测到 Agent 回调，消息已记录:', data);
  return false;
}

// 暴露到全局，供 Agent 注册回调
window.notifyAgent = notifyAgent;

/**
 * 获取当前 modelContext 面:WebMCP 规范面是 document.modelContext(Chrome 150+),
 * navigator.modelContext 作为废弃别名保留,优先取 document。
 */
function getModelContext() {
  return (typeof document !== 'undefined' && document.modelContext) || navigator.modelContext;
}

/**
 * 检查是否已加载 MCP-B Global 运行时
 */
function checkMcpBGlobal() {
  var mc = getModelContext();
  return typeof mc !== 'undefined' &&
    typeof mc.registerTool === 'function' &&
    typeof mc.callTool === 'function';
}

/**
 * 初始化 WebMCP 接口
 * 注册所有工具和资源到 navigator.modelContext
 */
function initWebMCP() {
  // 幂等保护:页面若重复加载 app bundle(CDN 旧缓存等),模块会实例化多份,须用全局标记跨实例去重
  if (window.__webmcpInited) {
    console.log('[WebMCP] already initialized, skip');
    return;
  }
  window.__webmcpInited = true;
  // 注入 sr-only 样式
  injectSrOnlyStyles();

  var usePolyfill = false;

  // 检查 MCP-B Global 是否已加载
  if (checkMcpBGlobal()) {
    console.log('[WebMCP] 检测到 MCP-B Global 运行时');
  }
  // 检查浏览器是否支持 WebMCP 或使用 polyfill
  else if (!('modelContext' in document) && !('modelContext' in navigator)) {
    console.log('[WebMCP] 浏览器不支持 WebMCP，使用 polyfill');
    usePolyfill = true;
  } else if (typeof getModelContext().callTool !== 'function') {
    // 浏览器有 modelContext 但缺少 callTool 方法，也需要 polyfill
    console.log('[WebMCP] 浏览器 WebMCP 不完整 (缺少 callTool)，使用 polyfill');
    usePolyfill = true;
  } else if (typeof getModelContext().listTools !== 'function') {
    // 浏览器有 modelContext 但缺少 listTools 方法，也需要 polyfill
    console.log('[WebMCP] 浏览器 WebMCP 不完整 (缺少 listTools)，使用 polyfill');
    usePolyfill = true;
  }

  if (usePolyfill) {
    navigator.modelContext = createPolyfill();
  }

  var mc = getModelContext() || navigator.modelContext;

  // 注册工具
  console.log('[WebMCP] 注册工具...');
  tools.forEach(function(tool) {
    mc.registerTool(tool);
    console.log('[WebMCP] 已注册工具: ' + tool.name);
  });

  // 注册资源（如果浏览器支持）
  if (typeof mc.registerResource === 'function') {
    console.log('[WebMCP] 注册资源...');
    resources.forEach(function(resource) {
      mc.registerResource(resource);
      console.log('[WebMCP] 已注册资源: ' + resource.uri);
    });
  } else {
    console.log('[WebMCP] 浏览器不支持 registerResource，跳过资源注册');
  }

  console.log('[WebMCP] 初始化完成');
  console.log('[WebMCP] 已注册 ' + tools.length + ' 个工具' + (typeof mc.registerResource === 'function' ? ', ' + resources.length + ' 个资源' : ''));

  // 标记页面已就绪
  window.webmcpReady = true;

  // 通知 Agent 页面已就绪
  // webmcp-a11y-expose.js 会自动暴露工具列表到无障碍树
  notifyAgent({
    event: 'page_ready',
    tools: tools.map(function(t) { return t.name; }),
    timestamp: Date.now()
  });
}

/**
 * 创建 WebMCP polyfill
 * 为不支持原生 WebMCP 的浏览器提供基本功能
 */
function createPolyfill() {
  var registeredTools = new Map();
  var registeredResources = new Map();

  return {
    // 注册工具
    registerTool: function(tool) {
      registeredTools.set(tool.name, tool);
    },

    // 注销工具
    unregisterTool: function(name) {
      registeredTools.delete(name);
    },

    // 列出工具
    listTools: function() {
      return Array.from(registeredTools.values()).map(function(tool) {
        return {
          name: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
        };
      });
    },

    // 调用工具（对象格式 { name, arguments }）
    callTool: function(params) {
      var name = params && params.name;
      var args = (params && params.arguments) || {};
      var tool = registeredTools.get(name);
      if (!tool) {
        return Promise.reject(new Error(t('工具未找到: ', 'Tool not found: ') + name));
      }
      return Promise.resolve(tool.execute(args));
    },

    // 注册资源
    registerResource: function(resource) {
      registeredResources.set(resource.uri, resource);
    },

    // 注销资源
    unregisterResource: function(uri) {
      registeredResources.delete(uri);
    },

    // 列出资源
    listResources: function() {
      return Array.from(registeredResources.values()).map(function(resource) {
        return {
          uri: resource.uri,
          name: resource.name,
          description: resource.description,
          mimeType: resource.mimeType,
        };
      });
    },

    // 读取资源
    readResource: function(uri) {
      var resource = registeredResources.get(uri);
      if (!resource) {
        return Promise.reject(new Error(t('资源未找到: ', 'Resource not found: ') + uri));
      }
      return Promise.resolve(resource.read());
    },
  };
}

module.exports = initWebMCP;
module.exports.initWebMCP = initWebMCP;
module.exports.default = initWebMCP;



// WEBPACK FOOTER //
// ./src/webmcp/index.js